<script setup>
defineOptions({ name: "BoletosPage" });
import { defineAsyncComponent } from "vue";
import {
  DocumentTextIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  RectangleGroupIcon,
  ShoppingBagIcon,
  WrenchScrewdriverIcon,
  TruckIcon,
  ArrowPathIcon,
} from "@heroicons/vue/24/outline";
import { useSeo } from "@/composables/useSeo";
import FaqPanel from "@/components/ui/FAQ.vue";
import SegmentHero from "@/components/ui/SegmentHero.vue";
import heroBg from "@/assets/hero-boletos.webp";

const CTA = defineAsyncComponent(() => import("@/components/home/CTA.vue"));
const portalUrl =
  import.meta.env?.VITE_PORTAL_URL || "https://portal.kalisistemas.com.br";

useSeo({
  title: "Boletos integrados à sua gestão financeira | Kali Sistemas",
  description:
    "Emita cobranças pelo Kali Gestão e mantenha recebimentos, vencimentos e contas a receber organizados em um único lugar.",
  image: heroBg,
});

// Revela o elemento com fade+slide assim que ele entra na viewport ao rolar a página.
const vReveal = {
  mounted(el) {
    el.classList.add("reveal-init");
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("reveal-visible");
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" },
    );
    io.observe(el);
  },
};

// Capacidades reais do adicional Boleto, descritas no material do produto.
const capacidades = [
  {
    icon: DocumentTextIcon,
    titulo: "Boleto integrado",
    texto: "Gere a cobrança dentro do Kali Gestão, conectada às informações do financeiro.",
  },
  {
    icon: BanknotesIcon,
    titulo: "Contas a receber",
    texto: "Acompanhe as cobranças junto aos demais valores que sua empresa tem para receber.",
  },
  {
    icon: CalendarDaysIcon,
    titulo: "Vencimentos",
    texto: "Visualize as datas das cobranças e tenha mais controle sobre os compromissos dos clientes.",
  },
  {
    icon: RectangleGroupIcon,
    titulo: "Gestão centralizada",
    texto: "Reduza a dependência de planilhas e controles financeiros separados.",
  },
];

const steps = [
  {
    title: "Venda ou lançamento financeiro",
    description: "A empresa realiza uma venda ou registra uma conta a receber no Kali Gestão.",
  },
  {
    title: "Geração da cobrança",
    description: "O boleto é gerado a partir das informações da cobrança, sem digitar de novo.",
  },
  {
    title: "Envio ao cliente",
    description: "A cobrança fica pronta para ser disponibilizada ao cliente.",
  },
  {
    title: "Acompanhamento",
    description: "O financeiro acompanha vencimentos e a situação de cada cobrança.",
  },
  {
    title: "Gestão financeira",
    description: "As informações permanecem organizadas dentro do fluxo financeiro da empresa.",
  },
];

// Tipos de negócio que mais se beneficiam da cobrança por boleto integrada ao financeiro.
const segmentos = [
  {
    icon: ShoppingBagIcon,
    titulo: "Comércio",
    texto: "Para quem vende produtos e precisa acompanhar vendas com pagamento futuro.",
  },
  {
    icon: WrenchScrewdriverIcon,
    titulo: "Prestadores de serviços",
    texto: "Para cobrança de serviços, mensalidades, contratos e outros recebimentos recorrentes.",
  },
  {
    icon: TruckIcon,
    titulo: "Distribuidoras",
    texto: "Para operações com vendas faturadas e acompanhamento dos recebimentos dos clientes.",
  },
  {
    icon: ArrowPathIcon,
    titulo: "Vendas recorrentes",
    texto: "Para negócios que precisam de mais previsibilidade e organização nas cobranças.",
  },
];

const faqItems = [
  {
    question: "O boleto fica integrado ao financeiro?",
    answer:
      "Sim. O recurso foi pensado para fazer parte do fluxo financeiro do Kali Gestão, facilitando o acompanhamento das cobranças e dos valores a receber.",
  },
  {
    question: "Posso acompanhar os vencimentos?",
    answer:
      "Sim. As informações das cobranças ficam disponíveis para acompanhamento dentro da gestão financeira.",
  },
  {
    question: "O recurso funciona junto ao contas a receber?",
    answer:
      "Sim. A cobrança por boleto complementa o fluxo de contas a receber do Kali Gestão, sem controles paralelos.",
  },
  {
    question: "Posso usar boleto mesmo já trabalhando com outras formas de pagamento?",
    answer:
      "Sim. O boleto pode compor a estratégia de cobrança da empresa junto com PIX e outras formas de recebimento.",
  },
  {
    question: "Como solicito a ativação do módulo?",
    answer:
      "Pelo botão \"Solicitar ativação\" desta página, que leva direto ao Portal do Cliente.",
  },
];
</script>

<template>
  <div class="min-h-screen bg-white">
    <SegmentHero
      title="Boletos integrados à sua gestão financeira"
      description="Emita cobranças pelo Kali Gestão e mantenha seus recebimentos organizados em um único lugar. Acompanhe valores a receber, vencimentos e a situação das cobranças sem depender de controles paralelos."
      primary-label="Solicitar ativação"
      :primary-href="portalUrl"
      :primary-opens-demo="false"
      secondary-label="Ver Kali Gestão"
      secondary-href="/solucao/kali-gestao"
      :background-image="heroBg"
      background-class="bg-slate-900 bg-cover bg-center"
      overlay-variant="left"
    />

    <!-- Visão geral -->
    <section class="py-16 bg-white scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2
            class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Não é apenas um boleto. É uma cobrança conectada ao seu financeiro.
          </h2>
          <p class="mt-6 text-lg leading-8 text-gray-600">
            Quando a cobrança acontece separada da gestão, acompanhar
            recebimentos pode exigir consultas ao banco, controles paralelos
            e conferências manuais. Com o módulo de Boletos, a cobrança passa
            a fazer parte do fluxo financeiro da empresa.
          </p>
        </div>

        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-4">
          <div
            v-for="cap in capacidades"
            :key="cap.titulo"
            v-reveal
            class="p-6 bg-[#e6f0f4] rounded-2xl"
          >
            <component :is="cap.icon" class="w-8 h-8 text-kali-blue-600" />
            <p class="mt-4 text-base font-bold text-gray-900">{{ cap.titulo }}</p>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">{{ cap.texto }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Como funciona -->
    <section class="py-16 bg-gray-100 scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Da cobrança ao controle financeiro em um único fluxo
          </h2>
          <p class="mt-4 text-base leading-7 text-gray-600">
            Da venda ao recebimento, sem retrabalho.
          </p>
        </div>

        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-5">
          <div
            v-for="(step, idx) in steps"
            :key="step.title"
            v-reveal
            class="p-7 bg-white rounded-3xl shadow-sm"
          >
            <div
              class="inline-flex items-center justify-center w-10 h-10 font-bold text-white rounded-xl bg-kali-blue-600"
            >
              {{ idx + 1 }}
            </div>
            <h3 class="mt-5 text-lg font-bold text-gray-900">{{ step.title }}</h3>
            <p class="mt-3 text-sm leading-relaxed text-gray-600">
              {{ step.description }}
            </p>
          </div>
        </div>

      </div>
    </section>

    <!-- Para quem é -->
    <section class="py-16 bg-white scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Cobranças organizadas para diferentes tipos de negócio
          </h2>
        </div>

        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-4">
          <div
            v-for="s in segmentos"
            :key="s.titulo"
            v-reveal
            class="p-6 bg-[#e6f0f4] rounded-2xl"
          >
            <component :is="s.icon" class="w-8 h-8 text-kali-blue-600" />
            <p class="mt-4 text-base font-bold text-gray-900">{{ s.titulo }}</p>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">{{ s.texto }}</p>
          </div>
        </div>
      </div>
    </section>

    <FaqPanel
      title="Dúvidas sobre Boletos"
      :items="faqItems"
      cta-label="Solicitar ativação"
      :cta-href="portalUrl"
      description="Respostas rápidas sobre o módulo de Boletos. Se preferir, fale com nosso time e te orientamos no melhor cenário."
    />

    <CTA :activation-href="portalUrl" />
  </div>
</template>

<style scoped>
.reveal-init {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}

.reveal-visible {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .reveal-init {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
